// FILE LOCATION: src/utils/documentProcessor.js

export async function processDocument(
  parsedDoc,
  visibility,
  usePlaceholders,
  stripRules = [],
  registry = {},
  assetOverrides = {},
  onProgress = null
) {
  // Yields control back to the UI so progress updates can paint
  const step = async (label) => {
    if (onProgress) await onProgress(label);
  };

  await step("Cleaning shortcodes…");
  processShortcodes(parsedDoc);
  await step("Assembling components…");
  processAssetBlocks(parsedDoc, registry, assetOverrides);
  await step("Wrapping code snippets…");
  processCodeSnippets(parsedDoc);
  await step("Converting embedded HTML…");
  processRawHTMLParagraphs(parsedDoc);
  await step("Removing Word footers…");
  removeWordDocumentFooter(parsedDoc);

  if (usePlaceholders) {
    await step("Adding image placeholders…");
    replaceImages(parsedDoc);
  }

  await step("Tidying anchors & links…");
  removeAnchorTags(parsedDoc);
  replaceLinkPlaceholders(parsedDoc);
  await step("Applying strip/remap rules…");
  applyStripRules(parsedDoc, stripRules, registry);
  await step("Processing bracket tags…");
  processBracketedElements(parsedDoc);
  processStyledElements(parsedDoc);
  convertInstH3ToP(parsedDoc);
  await step("Cleaning empty elements…");
  removeEmptyElements(parsedDoc);
  removeDevElements(parsedDoc, visibility);

  return parsedDoc;
}

function processShortcodes(parsedDoc) {
  // Phase 1: Clean quotes and un-nest shortcodes to the root body level
  const walker = parsedDoc.createTreeWalker(
    parsedDoc.body,
    NodeFilter.SHOW_TEXT,
    null
  );
  let node;
  const nodesToMove = [];

  while ((node = walker.nextNode())) {
    let text = node.nodeValue;
    text = text.replace(/[“”]/g, '"').replace(/\u00a0/g, " ");
    text = text.replace(/Character/g, "Image").replace(/character/g, "image");
    node.nodeValue = text;

    // Added source-wrapper and source-title to the regex engine
    const regex =
      /\[(row-start|col-split|row-end|\/?accordion-title|\/?accordion-wrapper[^\]]*|\/?source-title|\/?source-wrapper)\]/gi;
    let match;
    while ((match = regex.exec(text)) !== null) {
      nodesToMove.push({ node, index: match.index, length: match[0].length });
    }
  }

  // Split the tree and "bubble" the shortcodes up so they aren't trapped in <p> tags
  nodesToMove.reverse().forEach(({ node, index, length }) => {
    const middleNode = node.splitText(index);
    middleNode.splitText(length);

    let current = middleNode;
    while (current.parentNode && current.parentNode !== parsedDoc.body) {
      const parent = current.parentNode;
      const parentClone = parent.cloneNode(false);

      while (current.nextSibling) {
        parentClone.appendChild(current.nextSibling);
      }

      parent.parentNode.insertBefore(middleNode, parent.nextSibling);

      if (parentClone.childNodes.length > 0) {
        parent.parentNode.insertBefore(parentClone, middleNode.nextSibling);
      }

      if (parent.childNodes.length === 0) {
        parent.remove();
      }

      current = middleNode;
    }
  });

  // Phase 2: Linear Pass Layout Builder
  const childNodes = Array.from(parsedDoc.body.childNodes);
  let state = "normal";

  // Storage for our components being built
  let accWrapper, accTitleBtn, accTitleSpan, accContent;
  let rowWrapper, leftCol, rightCol;
  let sourceWrapper, sourceTitle, sourceContent;

  for (let i = 0; i < childNodes.length; i++) {
    const child = childNodes[i];

    // Detect shortcode triggers
    if (child.nodeType === 3) {
      // 3 = TEXT_NODE
      const text = child.nodeValue.trim();
      const lower = text.toLowerCase();

      // --- ACCORDION TRIGGERS ---
      const accWrapMatch = text.match(
        /^\[accordion-wrapper(?:[\s]+theme="([^"]*)")?[\s]*\]$/i
      );
      if (accWrapMatch) {
        let colorClass = accWrapMatch[1] ? accWrapMatch[1].trim() : "";
        colorClass = colorClass.replace("prime-", "accordion-");

        accWrapper = parsedDoc.createElement("div");
        accWrapper.className = `universal-base-accordion ${colorClass}`.trim();

        accTitleBtn = parsedDoc.createElement("button");
        accTitleBtn.className = "universal-base-accordion-title-button";
        accTitleBtn.setAttribute(
          "title",
          "Press 'a' to open & close all accordions"
        );
        accTitleSpan = parsedDoc.createElement("span");
        accTitleBtn.appendChild(accTitleSpan);

        accContent = parsedDoc.createElement("div");

        accWrapper.appendChild(accTitleBtn);
        accWrapper.appendChild(accContent);

        parsedDoc.body.insertBefore(accWrapper, child);
        child.remove();
        state = "accordion";
        continue;
      }

      if (lower === "[accordion-title]") {
        state = "accordion-title";
        child.remove();
        continue;
      }
      if (lower === "[/accordion-title]") {
        state = "accordion-body";
        child.remove();
        continue;
      }
      if (lower === "[/accordion-wrapper]") {
        state = "normal";
        child.remove();
        continue;
      }

      // --- COLUMN TRIGGERS ---
      if (lower === "[row-start]") {
        rowWrapper = parsedDoc.createElement("div");
        rowWrapper.className = "cont-r";
        leftCol = parsedDoc.createElement("div");
        rightCol = parsedDoc.createElement("div");

        rowWrapper.appendChild(leftCol);
        rowWrapper.appendChild(rightCol);

        parsedDoc.body.insertBefore(rowWrapper, child);
        state = "row-left";
        child.remove();
        continue;
      }
      if (lower === "[col-split]") {
        state = "row-right";
        child.remove();
        continue;
      }
      if (lower === "[row-end]") {
        state = "normal";
        child.remove();
        continue;
      }

      // --- SOURCE TRIGGERS ---
      if (lower === "[source-wrapper]") {
        sourceWrapper = parsedDoc.createElement("div");

        sourceTitle = parsedDoc.createElement("p");
        sourceTitle.className = "sourcetitle";

        sourceContent = parsedDoc.createElement("div");
        sourceContent.className = "source";

        sourceWrapper.appendChild(sourceTitle);
        sourceWrapper.appendChild(sourceContent);

        parsedDoc.body.insertBefore(sourceWrapper, child);
        state = "source";
        child.remove();
        continue;
      }
      if (lower === "[source-title]") {
        state = "source-title";
        child.remove();
        continue;
      }
      if (lower === "[/source-title]") {
        state = "source-body";
        child.remove();
        continue;
      }
      if (lower === "[/source-wrapper]") {
        state = "normal";
        child.remove();
        continue;
      }

      if (text === "") continue;
    }

    // Route content safely into the newly created structures
    if (state === "accordion-title") {
      if (child.tagName === "P") {
        while (child.firstChild) accTitleSpan.appendChild(child.firstChild);
        child.remove();
      } else {
        accTitleSpan.appendChild(child);
      }
    } else if (state === "accordion-body" || state === "accordion") {
      accContent.appendChild(child);
    } else if (state === "row-left") {
      leftCol.appendChild(child);
    } else if (state === "row-right") {
      rightCol.appendChild(child);
    } else if (state === "source-title") {
      if (child.tagName === "P") {
        // Unpack paragraphs so the p.sourcetitle stays valid HTML
        while (child.firstChild) sourceTitle.appendChild(child.firstChild);
        child.remove();
      } else {
        sourceTitle.appendChild(child);
      }
    } else if (state === "source-body" || state === "source") {
      sourceContent.appendChild(child);
    }
  }
}

// ── Registry fuzzy matching helpers ───────────────────────────────────────────
// Strips "Default", lowercases, removes non-alphanumeric so titles and triggers
// can be compared loosely:  "Alert Boxes" → "alertboxes",  "ALERT BOX" → "alertbox"
function normalizeForMatch(str) {
  return str
    .toLowerCase()
    .replace(/^default\s+/i, "")
    .replace(/[^a-z0-9]/g, "");
}

// Returns { key, item } for the best registry match, or null if nothing fits.
// Matches when one normalized string is a prefix of the other (handles plurals,
// abbreviations, etc.) and at least 60 % of the longer one is covered.
function findRegistryMatch(triggerText, registry) {
  if (!registry || !triggerText) return null;
  const tn = normalizeForMatch(triggerText);
  if (!tn) return null;

  let best = null,
    bestScore = 0;
  for (const [key, item] of Object.entries(registry)) {
    const rn = normalizeForMatch(item.title);
    if (!rn) continue;
    if (rn === tn || rn.startsWith(tn) || tn.startsWith(rn)) {
      const score =
        Math.min(tn.length, rn.length) / Math.max(tn.length, rn.length);
      if (score >= 0.6 && score > bestScore) {
        bestScore = score;
        best = { key, item };
      }
    }
  }
  return best;
}

// Maps a class value to its slot name by prefix convention.
// theme-red-500   → COLOR_CLASS
// icon-disc-info  → ICON_CLASS
// radius-lg       → RADIUS_CLASS
// gradient-mid    → GRADIENT_CLASS  (or COLOR_CLASS if you prefer — adjust below)
// border-thick    → BORDER_CLASS
// shadow-sm       → SHADOW_CLASS
// light / dark    → LIGHT_CLASS
function classToSlot(value) {
  if (value.startsWith("theme-")) return "COLOR_CLASS";
  if (value.startsWith("icon-")) return "ICON_CLASS";
  if (value.startsWith("radius-")) return "RADIUS_CLASS";
  if (value.startsWith("gradient-")) return "GRADIENT_CLASS";
  if (value.startsWith("border-")) return "BORDER_CLASS";
  if (value.startsWith("shadow-")) return "SHADOW_CLASS";
  if (value === "light" || value === "dark") return "LIGHT_CLASS";
  return null;
}

// Parses the opening line of a block, e.g. "[ALERT BOX theme-red-500 radius-lg icon-info"
// Returns { triggerText, overrides } where overrides is a map of slot → class.
// Recognised class prefixes are stripped from the trigger text; anything left
// becomes the asset name to look up in the registry.
function parseOpeningLine(text) {
  const inner = text.slice(1).trim(); // strip leading [
  const parts = inner.split(/\s+/);

  const overrides = {};
  const triggerWords = [];

  for (const part of parts) {
    const slot = classToSlot(part);
    if (slot) {
      overrides[slot] = part;
    } else {
      triggerWords.push(part);
    }
  }

  return {
    triggerText: triggerWords.join(" "),
    overrides,
  };
}

// Assembles an asset item into an HTML string, filling %%TOKENS%% and injecting
// title / content into the DOM via CSS selectors.
// `overrides` may use either camelCase keys (from TXT @-lines) or UPPER_SNAKE
// keys (from inline bracket triggers). Both are normalised here.
function assembleItem(
  parsedDoc,
  item,
  overrides,
  inlineOverrides,
  title,
  content
) {
  const toUpperSnake = (k) => k.replace(/([A-Z])/g, "_$1").toUpperCase();

  const slots = {};
  Object.entries(item.defaults || {}).forEach(([k, v]) => {
    slots[toUpperSnake(k)] = v;
  });
  Object.entries(overrides || {}).forEach(([k, v]) => {
    slots[toUpperSnake(k)] = v;
  });
  Object.entries(inlineOverrides || {}).forEach(([k, v]) => {
    // inlineOverrides keys are already UPPER_SNAKE
    slots[k] = v;
  });

  const html = item.code.replace(/%%([A-Z_]+)%%/g, (_, t) =>
    slots[t] !== undefined ? slots[t] : ""
  );

  const wrapper = parsedDoc.createElement("div");
  wrapper.innerHTML = html.trim();
  const d = item.defaults || {};

  if (d.itemSeparator && content) {
    // repeating handled upstream — skip here
  } else {
    if (d.titleSelector && title) {
      const el = wrapper.querySelector(d.titleSelector);
      if (el) el.textContent = title;
    }
    if (d.contentSelector && content) {
      const el = wrapper.querySelector(d.contentSelector);
      if (el) el.innerHTML = content;
    }
  }
  return wrapper;
}

// Detects [TRIGGER ... ] blocks where TRIGGER matches a key in the asset registry,
// assembles the component HTML, and replaces the block with live DOM nodes.
// Must run before processCodeSnippets so code-snippet fallback doesn't consume them first.
function processAssetBlocks(parsedDoc, registry, assetOverrides) {
  if (!registry || Object.keys(registry).length === 0) return;

  const body = parsedDoc.body;
  const children = Array.from(body.childNodes);

  let inBlock = false;
  let triggerKey = "";
  let inlineOverrides = {};
  let contentNodes = [];
  let blockNodes = [];
  let insertBeforeNode = null;

  // ── Repeating assets (tabs, carousel) ─────────────────────────────────────
  // Splits contentNodes on paragraphs starting with the itemSeparator (e.g. "TITLE:")
  // Returns [{ title, content }, ...]
  const parseRepeatingContent = (nodes, separator) => {
    const sections = [];
    let currentTitle = null;
    let currentContent = [];

    const save = () => {
      if (currentTitle !== null) {
        sections.push({
          title: currentTitle,
          content: currentContent
            .map((n) => n.outerHTML || n.textContent)
            .join("\n"),
        });
      }
    };

    for (const node of nodes) {
      const text = (node.textContent || "").trim();
      if (text.toUpperCase().startsWith(separator.toUpperCase())) {
        save();
        currentTitle = text.slice(separator.length).trim();
        currentContent = [];
      } else {
        currentContent.push(node);
      }
    }
    save();
    return sections;
  };

  // Pattern A — split containers (tabs): separate header buttons + content panels
  const assembleSplitContainer = (wrapper, d, sections) => {
    const titleContainer = wrapper.querySelector(d.itemTitleContainerSelector);
    const contentContainer = wrapper.querySelector(
      d.itemContentContainerSelector
    );
    if (!titleContainer || !contentContainer) return;

    const btnTemplate = titleContainer.querySelector(d.itemTitleSelector);
    const panelTemplate = contentContainer.querySelector(d.itemContentSelector);
    if (!btnTemplate || !panelTemplate) return;

    titleContainer.innerHTML = "";
    contentContainer.innerHTML = "";

    sections.forEach((section, idx) => {
      const btn = btnTemplate.cloneNode(true);
      const panel = panelTemplate.cloneNode(true);

      btn.textContent = section.title;
      panel.innerHTML = section.content || "<p></p>";

      if (d.activeClass) {
        btn.classList.remove(d.activeClass);
        panel.classList.remove(d.activeClass);
        if (idx === 0) {
          btn.classList.add(d.activeClass);
          panel.classList.add(d.activeClass);
        }
      }

      titleContainer.appendChild(btn);
      contentContainer.appendChild(panel);
    });
  };

  // Pattern B — single container (carousel): one container, each item cloned whole
  const assembleSingleContainer = (wrapper, d, sections) => {
    const container = wrapper.querySelector(d.itemContainerSelector);
    if (!container) return;

    const itemTemplate = container.querySelector(d.itemSelector);
    if (!itemTemplate) return;

    container.innerHTML = "";

    sections.forEach((section, idx) => {
      const item = itemTemplate.cloneNode(true);

      if (d.itemTitleSelector) {
        const titleEl = item.querySelector(d.itemTitleSelector);
        if (titleEl) titleEl.textContent = section.title;
      }

      // Put content into the first <p> inside the item, or the item itself
      const firstP = item.querySelector("p");
      if (firstP) firstP.innerHTML = section.content || "";

      if (d.activeClass && idx === 0) item.classList.add(d.activeClass);

      container.appendChild(item);
    });
  };

  // ── Flush: assemble and insert the completed block ─────────────────────────
  const flush = () => {
    if (!insertBeforeNode || !triggerKey) return;
    const item = registry[triggerKey];
    if (!item) return;

    const overrides = assetOverrides[triggerKey] || {};
    const d = item.defaults || {};

    if (d.itemSeparator && contentNodes.length > 0) {
      // ── Repeating asset (tabs, carousel) ──
      const toUpperSnake = (k) => k.replace(/([A-Z])/g, "_$1").toUpperCase();
      const slots = {};
      Object.entries(d).forEach(([k, v]) => {
        slots[toUpperSnake(k)] = v;
      });
      Object.entries(overrides).forEach(([k, v]) => {
        slots[toUpperSnake(k)] = v;
      });
      Object.entries(inlineOverrides).forEach(([k, v]) => {
        slots[k] = v;
      });

      const html = item.code.replace(
        /%%([A-Z_]+)%%/g,
        (_, t) => slots[t] ?? ""
      );
      const wrapper = parsedDoc.createElement("div");
      wrapper.innerHTML = html.trim();

      const sections = parseRepeatingContent(contentNodes, d.itemSeparator);
      if (sections.length > 0) {
        if (d.itemTitleContainerSelector && d.itemContentContainerSelector) {
          assembleSplitContainer(wrapper, d, sections);
        } else if (d.itemContainerSelector && d.itemSelector) {
          assembleSingleContainer(wrapper, d, sections);
        }
      }

      while (wrapper.firstChild) {
        insertBeforeNode.parentNode.insertBefore(
          wrapper.firstChild,
          insertBeforeNode
        );
      }
    } else {
      // ── Simple asset (accordion, reveal, callout, etc.) ──
      const titleNode = contentNodes[0];
      const title = titleNode ? titleNode.textContent.trim() : "";
      const content = contentNodes
        .slice(1)
        .map((n) => n.outerHTML || n.textContent)
        .join("\n");

      const wrapper = assembleItem(
        parsedDoc,
        item,
        overrides,
        inlineOverrides,
        title,
        content
      );
      while (wrapper.firstChild) {
        insertBeforeNode.parentNode.insertBefore(
          wrapper.firstChild,
          insertBeforeNode
        );
      }
    }

    blockNodes.forEach((n) => {
      if (n.parentNode) n.remove();
    });

    inBlock = false;
    triggerKey = "";
    inlineOverrides = {};
    contentNodes = [];
    blockNodes = [];
    insertBeforeNode = null;
  };

  for (const child of children) {
    const trimmed = (child.textContent || "").trim();

    if (!inBlock) {
      if (trimmed.startsWith("[") && !trimmed.endsWith("]")) {
        const { triggerText, overrides: parsedOverrides } =
          parseOpeningLine(trimmed);
        // Reserved triggers — these belong to processCodeSnippets, not the asset registry
        const isReserved = /^(HTML|EMBED|VIDEO|IMAGE|USE\b|ADD\b)/i.test(
          triggerText
        );
        const match = !isReserved
          ? findRegistryMatch(triggerText, registry)
          : null;
        if (match) {
          inBlock = true;
          triggerKey = match.key;
          inlineOverrides = parsedOverrides;
          insertBeforeNode = child;
          blockNodes.push(child);
        }
      }
    } else {
      blockNodes.push(child);
      if (trimmed === "]") {
        flush();
      } else {
        if (child.nodeType === 1) contentNodes.push(child);
      }
    }
  }
}

// Detects paragraphs whose text content looks like raw HTML markup (the author
// pasted HTML directly into Word without an [HTML ... ] wrapper) and converts
// them to live HTML. Also merges consecutive paragraphs into one parse attempt
// when Word split an embed across paragraph breaks.
function processRawHTMLParagraphs(parsedDoc) {
  const tryReplace = (paragraphs) => {
    const combined = paragraphs
      .map((p) => (p.textContent || "").trim())
      .filter(Boolean)
      .join("\n")
      .trim();
    if (!combined || !combined.includes("<") || !combined.includes(">")) return false;

    const temp = parsedDoc.createElement("div");
    temp.innerHTML = combined;

    const elements = Array.from(temp.querySelectorAll("*"));
    const hasComment = Array.from(temp.childNodes).some((n) => n.nodeType === 8);
    if (elements.length === 0 && !hasComment) return false;

    const validTags = new Set([
      "a", "abbr", "address", "area", "article", "aside", "audio", "b", "base", "bdi", "bdo", "blockquote", 
      "body", "br", "button", "canvas", "caption", "cite", "code", "col", "colgroup", "data", "datalist", 
      "dd", "del", "details", "dfn", "dialog", "div", "dl", "dt", "em", "embed", "fieldset", "figcaption", 
      "figure", "footer", "form", "h1", "h2", "h3", "h4", "h5", "h6", "head", "header", "hgroup", "hr", 
      "html", "i", "iframe", "img", "input", "ins", "kbd", "label", "legend", "li", "link", "main", "map", 
      "mark", "menu", "meta", "meter", "nav", "noscript", "object", "ol", "optgroup", "option", "output", 
      "p", "picture", "pre", "progress", "q", "rp", "rt", "ruby", "s", "samp", "script", "section", "select", 
      "slot", "small", "source", "span", "strong", "style", "sub", "summary", "sup", "table", "tbody", "td", 
      "template", "textarea", "tfoot", "th", "thead", "time", "title", "tr", "track", "u", "ul", "var", 
      "video", "wbr", "svg", "path", "g", "polygon", "rect", "circle", "line", "polyline", "ellipse"
    ]);

    const allValid = elements.every((el) => {
      const tag = el.tagName.toLowerCase();
      return validTags.has(tag) || tag.includes("-");
    });
    if (!allValid) return false;

    const first = paragraphs[0];
    const parent = first.parentNode;
    if (!parent) return false;

    if (first.tagName !== "P") {
      first.innerHTML = "";
      while (temp.firstChild) {
        first.appendChild(temp.firstChild);
      }
      paragraphs.slice(1).forEach((p) => {
        if (p.parentNode) {
          p.remove();
        }
      });
    } else {
      while (temp.firstChild) {
        parent.insertBefore(temp.firstChild, first);
      }
      paragraphs.forEach((p) => {
        if (p.parentNode) {
          p.remove();
        }
      });
    }
    return true;
  };

  const paragraphs = Array.from(parsedDoc.querySelectorAll("p, li, td, th, h1, h2, h3, h4, h5, h6"));
  const processed = new Set();

  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i];
    if (processed.has(p) || !p.parentNode) continue;

    const text = (p.textContent || "").trim();
    if (!/<[a-z!/]/i.test(text)) continue;

    const runElements = [p];
    let sibling = p.nextSibling;
    while (sibling) {
      if (sibling.nodeType === 3 && !(sibling.nodeValue || "").trim()) {
        sibling = sibling.nextSibling;
        continue;
      }
      if (sibling.nodeType === 1 && sibling.tagName === p.tagName) {
        runElements.push(sibling);
        sibling = sibling.nextSibling;
      } else {
        break;
      }
    }

    let replaced = false;
    for (let len = runElements.length; len >= 1; len--) {
      const subRun = runElements.slice(0, len);
      const lastText = (subRun[subRun.length - 1].textContent || "").trim();
      if (len > 1 && !lastText.endsWith(">")) {
        continue;
      }
      if (tryReplace(subRun)) {
        subRun.forEach((item) => processed.add(item));
        replaced = true;
        break;
      }
    }

    if (!replaced) {
      processed.add(p);
    }
  }
}

// Handles two kinds of multi-paragraph bracket blocks:
//
//   [HTML              → content is inserted as live HTML in a <div>
//   <div>...</div>
//   ]
//
//   [EMBED             → content is displayed as code in <pre class="code-snippet"><code>
//   <iframe ...>
//   ]
//
// Any top-level paragraph that starts with [ but doesn't close with ] on the same line
// triggers collection. The opening label is stripped; everything after it is the content.
function processCodeSnippets(parsedDoc) {
  const body = parsedDoc.body;

  let inBlock = false;
  let blockType = "code"; // 'html', 'video', 'image' or 'code'
  let lines = [];
  let blockNodes = [];
  let insertBeforeNode = null;

  const flush = () => {
    if (!insertBeforeNode) return;
    const content = lines.join("\n").trim();

    if (
      blockType === "html" ||
      blockType === "video" ||
      blockType === "image"
    ) {
      // Insert as live HTML — unwrap so the iframe (or whatever) is dropped in directly
      const temp = parsedDoc.createElement("div");
      temp.innerHTML = content;

      if (blockType === "video" || blockType === "image") {
        temp
          .querySelectorAll("img")
          .forEach((img) => img.setAttribute("data-no-placeholder", "true"));
      }

      while (temp.firstChild) {
        insertBeforeNode.parentNode.insertBefore(
          temp.firstChild,
          insertBeforeNode
        );
      }
    } else {
      // Insert as a code display block
      const pre = parsedDoc.createElement("pre");
      pre.className = "code-snippet";
      const code = parsedDoc.createElement("code");
      code.textContent = content;
      pre.appendChild(code);
      insertBeforeNode.parentNode.insertBefore(pre, insertBeforeNode);
    }

    blockNodes.forEach((n) => {
      if (n.parentNode) n.remove();
    });
    lines = [];
    blockNodes = [];
    insertBeforeNode = null;
    inBlock = false;
  };

  // Strip the opening label (e.g. "[HTML", "[EMBED", "[USE THIS EMBED CODE:")
  // leaving just the content that follows on the same line.
  // Only treat a colon as a label separator if it appears BEFORE the first <
  // (i.e. it's part of the label, not a URL like https://)
  const stripLabel = (text) => {
    const firstAngle = text.indexOf("<");
    const colonIdx = text.indexOf(":");
    if (colonIdx !== -1 && (firstAngle === -1 || colonIdx < firstAngle)) {
      return text.slice(colonIdx + 1).trim();
    }
    return text.replace(/^\[[A-Z\s]+/i, "").trim();
  };

  const children = Array.from(body.childNodes);

  for (const child of children) {
    const trimmed = (child.textContent || "").trim();

    if (!inBlock) {
      // Case A: entire [HTML ... ] block lives in a single element (Word soft returns → <br> tags,
      // so textContent starts with [HTML and ends with ] on the same node)
      const singleMatch = trimmed.match(/^\[(HTML|VIDEO|IMAGE)\b/i);
      if (singleMatch && trimmed.endsWith("]")) {
        const tag = singleMatch[1].toUpperCase();
        const content = trimmed
          .replace(/^\[[A-Z]+\s*/i, "")
          .slice(0, -1)
          .trim();
        if (content) {
          const temp = parsedDoc.createElement("div");
          temp.innerHTML = content;
          if (tag === "VIDEO" || tag === "IMAGE") {
            temp
              .querySelectorAll("img")
              .forEach((img) =>
                img.setAttribute("data-no-placeholder", "true")
              );
          }
          while (temp.firstChild) {
            child.parentNode.insertBefore(temp.firstChild, child);
          }
          child.remove();
        }
        continue;
      }

      // Case B: multi-paragraph block — starts with [ but does NOT close on the same node
      if (trimmed.startsWith("[") && !trimmed.endsWith("]")) {
        inBlock = true;
        if (/^\[HTML\b/i.test(trimmed)) blockType = "html";
        else if (/^\[VIDEO\b/i.test(trimmed)) blockType = "video";
        else if (/^\[IMAGE\b/i.test(trimmed)) blockType = "image";
        else blockType = "code";

        insertBeforeNode = child;
        blockNodes.push(child);
        const contentStart = stripLabel(trimmed);
        if (contentStart) lines.push(contentStart);
      }
    } else {
      blockNodes.push(child);
      if (trimmed === "]") {
        flush();
      } else if (trimmed.endsWith("]") && trimmed.length > 1) {
        lines.push(trimmed.slice(0, -1).trim());
        flush();
      } else {
        if (trimmed) lines.push(trimmed);
      }
    }
  }
}

// Inline HTML tags that can be used directly instead of <span class="...">
const INLINE_TAGS = new Set([
  "mark",
  "strong",
  "em",
  "code",
  "s",
  "u",
  "small",
  "abbr",
  "kbd",
  "cite",
]);

// Wraps the first occurrence of `phrase` inside `el`.
// If `target` is a known inline tag (e.g. "mark"), uses <mark>phrase</mark>.
// Otherwise wraps in <span class="target">phrase</span>.
function wrapPhrase(el, phrase, target, parsedDoc) {
  const tagName = INLINE_TAGS.has(target) ? target : "span";
  const classAttr = tagName === "span" ? ` class="${target}"` : "";
  const openTag = `<${tagName}${classAttr}>`;
  const closeTag = `</${tagName}>`;

  // Pass 1: look for the phrase inside a single text node (clean case)
  const walker = parsedDoc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    const idx = node.nodeValue.indexOf(phrase);
    if (idx !== -1) {
      const before = node.nodeValue.slice(0, idx);
      const after = node.nodeValue.slice(idx + phrase.length);
      const wrapper = parsedDoc.createElement(tagName);
      if (tagName === "span") wrapper.className = target;
      wrapper.textContent = phrase;
      const parent = node.parentNode;
      if (before) parent.insertBefore(parsedDoc.createTextNode(before), node);
      parent.insertBefore(wrapper, node);
      if (after) parent.insertBefore(parsedDoc.createTextNode(after), node);
      node.remove();
      return;
    }
  }

  // Pass 2: phrase is split across inline elements (e.g. Word formatting runs).
  // Work directly on innerHTML — try the HTML-entity-encoded version first,
  // then the plain version.
  const encoded = phrase
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  if (el.innerHTML.includes(encoded)) {
    el.innerHTML = el.innerHTML.replace(
      encoded,
      `${openTag}${encoded}${closeTag}`
    );
  } else if (el.innerHTML.includes(phrase)) {
    el.innerHTML = el.innerHTML.replace(
      phrase,
      `${openTag}${phrase}${closeTag}`
    );
  }
}

// Each rule is { phrase, className }
// className is an asset name      → assembles the matching registry component
// className is an inline tag name → <mark>, <strong>, <em>, etc.
// className is a CSS class name   → <span class="className">
// className absent                → <span class="hide">
function applyStripRules(parsedDoc, rules, registry = {}) {
  if (!rules || rules.length === 0) return;

  parsedDoc.querySelectorAll("p, h1, h2, h3, h4, li").forEach((el) => {
    for (const rule of rules) {
      if (el.textContent.includes(rule.phrase)) {
        const target = rule.className || "hide";

        // Check if target looks like an asset name (with optional class overrides).
        // CSS class names are lowercase (e.g. "alert-box", "dev").
        // Asset names are in CAPS or mixed case (e.g. "ALERT BOX theme-red-500 icon-info").
        // Only attempt a registry lookup when the target contains an uppercase letter.
        const inline = parseOpeningLine("[" + target);
        const assetName = inline.triggerText;
        const overridesMap = inline.overrides;
        const assetMatch = /[A-Z]/.test(assetName)
          ? findRegistryMatch(assetName, registry)
          : null;

        if (assetMatch) {
          // Replace the whole element with the assembled asset then stop —
          // the element is gone so no further rules can apply to it
          const parent = el.parentNode;
          if (parent) {
            const wrapper = assembleItem(
              parsedDoc,
              assetMatch.item,
              {},
              overridesMap,
              "",
              el.innerHTML
            );
            while (wrapper.firstChild) {
              parent.insertBefore(wrapper.firstChild, el);
            }
            el.remove();
          }
          break;
        } else {
          wrapPhrase(el, rule.phrase, target, parsedDoc);
          // No break — multiple non-asset rules can apply to the same element
        }
      }
    }
  });
}

function removeWordDocumentFooter(parsedDoc) {
  parsedDoc.querySelectorAll("sup").forEach((sup) => {
    const text = sup.textContent.trim();
    if (/^[\^_\s]+$/.test(text)) {
      sup.remove();
    }
  });

  parsedDoc.querySelectorAll("p").forEach((p) => {
    const text = p.textContent.trim();
    if (/^[\^_\s]+$/.test(text)) {
      p.remove();
    }
  });
}

function replaceImages(parsedDoc) {
  const placeholderHTML = `
    <div class="img-cap-container" style="width: 50%;">
      <div class="shadow-img img-c">
        <img class="screen-img " src="../images/place-holder-image-sq.png" alt="Place holder image">
      </div>
      <div class="text-c">
        <p>Image caption goes here.</p>
      </div>
    </div>
  `;

  parsedDoc.querySelectorAll("img").forEach((img) => {
    // Skip if marked as "no placeholder"
    if (img.getAttribute("data-no-placeholder") === "true") return;

    const tempDiv = parsedDoc.createElement("div");
    tempDiv.innerHTML = placeholderHTML.trim();
    const replacementNode = tempDiv.firstElementChild;

    if (img.parentNode && img.parentNode.tagName.toLowerCase() === "p") {
      const parent = img.parentNode;
      if (parent.children.length === 1 && parent.textContent.trim() === "") {
        parent.parentNode.replaceChild(replacementNode, parent);
        return;
      }
    }

    if (img.parentNode) {
      img.parentNode.replaceChild(replacementNode, img);
    }
  });
}

function removeAnchorTags(parsedDoc) {
  parsedDoc.querySelectorAll('h2 a, h3 a, a[id^="_Toc"]').forEach((a) => {
    a.remove();
  });
}

function replaceLinkPlaceholders(parsedDoc) {
  const walker = parsedDoc.createTreeWalker(
    parsedDoc.body,
    NodeFilter.SHOW_TEXT,
    null
  );

  const nodesToReplace = [];

  let node = walker.nextNode();
  while (node) {
    if (node.textContent.includes("(Link)")) {
      nodesToReplace.push(node);
    }
    node = walker.nextNode();
  }

  nodesToReplace.forEach((textNode) => {
    const parent = textNode.parentNode;
    const text = textNode.textContent;

    if (text.trim() === "(Link)") {
      const warningBox = parsedDoc.createElement("p");
      warningBox.className = "warning-box";
      warningBox.textContent = "(link to come)";
      parent.parentNode.replaceChild(warningBox, parent);
    } else {
      const parts = text.split("(Link)");
      const fragment = parsedDoc.createDocumentFragment();

      parts.forEach((part, index) => {
        if (part) {
          fragment.appendChild(parsedDoc.createTextNode(part));
        }
        if (index < parts.length - 1) {
          const warningBox = parsedDoc.createElement("p");
          warningBox.className = "warning-box";
          warningBox.textContent = "(link to come)";
          fragment.appendChild(warningBox);
        }
      });

      parent.replaceChild(fragment, textNode);
    }
  });
}

function processBracketedElements(parsedDoc) {
  parsedDoc.querySelectorAll("p, h3").forEach((el) => {
    const text = el.textContent.trim();

    // Single-bracket [ADD ...] pattern → class="id"
    const addMatch = text.match(/^\[ADD\s+(.+)\]$/i);
    if (addMatch) {
      const savedMedia = Array.from(el.querySelectorAll("img, iframe"));
      const newP = parsedDoc.createElement("p");
      newP.className = "id";
      newP.textContent = text.slice(1, -1).trim(); // keep full inner text including "ADD ..."
      savedMedia.forEach((media) => newP.appendChild(media));
      el.parentNode.replaceChild(newP, el);
      return;
    }

    // Multi-bracket patterns [[...]], [[[...]]], [[[[...]]]]
    const match = text.match(/^(\[+)(.+?)(\]+)$/);
    if (match) {
      const leftCount = match[1].length;
      const rightCount = match[3].length;

      if (leftCount === rightCount && leftCount >= 2 && leftCount <= 4) {
        let newClass = "";
        if (leftCount === 2) newClass = "dev";
        else if (leftCount === 3) newClass = "id";
        else if (leftCount === 4) newClass = "inst";

        const savedMedia = Array.from(el.querySelectorAll("img, iframe"));

        const newP = parsedDoc.createElement("p");
        newP.className = newClass;
        newP.textContent = match[2].trim();

        savedMedia.forEach((media) => newP.appendChild(media));

        el.parentNode.replaceChild(newP, el);
      }
    }
  });
}

function processStyledElements(parsedDoc) {
  parsedDoc.querySelectorAll("p, h3").forEach((el) => {
    let text = el.textContent.trim();
    const rescueMedia = () => Array.from(el.querySelectorAll("img, iframe"));

    if (text.startsWith("§@") && text.endsWith("@§")) {
      const content = text.slice(2, -2).trim();
      const media = rescueMedia();
      const newP = parsedDoc.createElement("p");
      newP.className = "dev";
      newP.textContent = content;
      media.forEach((m) => newP.appendChild(m));
      el.parentNode.replaceChild(newP, el);
      return;
    }

    if (text.startsWith("Ω@") && text.endsWith("@Ω")) {
      const content = text.slice(2, -2).trim();
      const media = rescueMedia();
      const newP = parsedDoc.createElement("p");
      newP.className = "id";
      newP.textContent = content;
      media.forEach((m) => newP.appendChild(m));
      el.parentNode.replaceChild(newP, el);
      return;
    }

    if (text.startsWith("π@") && text.endsWith("@π")) {
      const content = text.slice(2, -2).trim();
      const media = rescueMedia();
      const newP = parsedDoc.createElement("p");
      newP.className = "inst";
      newP.textContent = content;
      media.forEach((m) => newP.appendChild(m));
      el.parentNode.replaceChild(newP, el);
      return;
    }

    if (text.startsWith("©@") && text.endsWith("@©")) {
      const content = text.slice(2, -2).trim();
      const container = parsedDoc.createElement("div");
      container.setAttribute("data-code", "true");
      container.innerHTML = content;
      el.parentNode.replaceChild(container, el);
      return;
    }

    if (text.startsWith("®@") && text.endsWith("@®")) {
      const content = text.slice(2, -2).trim();

      if (el.tagName === "H1") {
        el.textContent = el.textContent.replace(/®@[^@]+@®/, "").trim();
        const newP = parsedDoc.createElement("p");
        newP.className = "resource";
        const a = parsedDoc.createElement("a");
        a.href = `../resources/${content}`;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = content;
        newP.appendChild(a);
        el.parentNode.insertBefore(newP, el.nextSibling);
      } else {
        const newP = parsedDoc.createElement("p");
        newP.className = "resource";
        const a = parsedDoc.createElement("a");
        a.href = `../resources/${content}`;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = content;
        newP.appendChild(a);
        el.parentNode.replaceChild(newP, el);
      }
      return;
    }
  });
}

function convertInstH3ToP(parsedDoc) {
  const h3InstEls = parsedDoc.querySelectorAll("h3.inst");
  h3InstEls.forEach((el) => {
    const p = parsedDoc.createElement("p");
    p.className = "inst";
    p.innerHTML = el.innerHTML;
    el.parentNode.replaceChild(p, el);
  });
}

function removeEmptyElements(parsedDoc) {
  parsedDoc.querySelectorAll("p, h3, em").forEach((el) => {
    const hasMedia = el.querySelector("img, iframe, video, audio") !== null;
    if (el.textContent.trim() === "" && !hasMedia) {
      el.remove();
    }
  });
}

function removeDevElements(parsedDoc, visibility) {
  if (visibility.all || visibility.dev) {
    parsedDoc.querySelectorAll(".dev").forEach((el) => el.remove());
  }
  if (visibility.all || visibility.id) {
    parsedDoc.querySelectorAll(".id").forEach((el) => el.remove());
  }
  if (visibility.all || visibility.inst) {
    parsedDoc.querySelectorAll(".inst").forEach((el) => el.remove());
  }
}

// Banners
const bannerSnippets = {
  none: "",
  image: `
<div>
  <img src="/ZZ_technical-files/images/page-banner-footer/page-banner.png" alt="Landscape" title="Banner" data-d2l-editor-default-img-style="true">
</div>`,
  iframe: `<div class="iframe-holder" id="iframe-page-banner"></div>`,
  bannerTimeline: `
<div>
  <img src="/ZZ_technical-files/images/page-banner-footer/page-banner.png" alt="Landscape" title="Banner" data-d2l-editor-default-img-style="true">
</div>
<div class="timeline-container-alt" id="timelineContainerAlt"></div>`,
  bannerPagination: `
<div>
  <img src="/ZZ_technical-files/images/page-banner-footer/page-banner.png" alt="Landscape" title="Banner" data-d2l-editor-default-img-style="true">
</div>
<div id="moduleProgressNew" class="module-progress-new"></div>
<div id="timelineContainerNew" class="timeline-container-new"></div>`,
  progressive: `
<div class="timeline-banner">
  <div id="tl-grid" class="image-overlay-tl"></div>
  <img src="/ZZ_technical-files/images/page-banner-footer/page-banner.png" alt="Landscape" title="Banner" data-d2l-editor-default-img-style="true">
</div>
<br><p id="result"></p>`,
  timeline: `
<div class="timeline-container-alt" id="timelineContainerAlt"></div>`,
  pagination: `
<div id="moduleProgressNew" class="module-progress-new"></div>
<div id="timelineContainerNew" class="timeline-container-new"></div>`,
};

// Footers
const footerSnippets = {
  None: "",
  ClassicDiamond: `<p class="endFlourish"><span class="logo"><img class="displayed" src="../cel/images/unbLogo.png" alt="UNB logo" width="15" height="14"></span></p>`,
  Image: `<div>\n  <img src="/ZZ_technical-files/images/page-banner-footer/page-banner-footer.png" alt="Landscape" title="Footer" data-d2l-editor-default-img-style="true">\n</div>`,
  Iframe: `<div class="iframe-holder" id="iframe-page-footer"></div>`,
};

export function createHTMLFile(
  title,
  content,
  bannerType,
  footerType,
  _prefixType,
  useLegacy = false,
  customCssPath = ""
) {
  const bannerHtml = bannerSnippets[bannerType] || "";
  const footerHtml = footerSnippets[footerType] || "";
  const themePrefix = useLegacy ? "prime-theme" : "main-theme";
  const customCssLink = customCssPath
    ? `\n  <link href="${customCssPath}" rel="stylesheet" type="text/css">`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <link href="/shared/online/styles/standardized/${themePrefix}.css" rel="stylesheet" type="text/css">
  <noscript><link href="/shared/online/styles/standardized/${themePrefix}-no-script.css" rel="stylesheet" type="text/css"></noscript>
  <link href="/shared/online/styles/standardized/anim-on-scroll.css" rel="stylesheet" type="text/css">${customCssLink}
</head>
<body>
${bannerHtml.trim()}
<main>${content}</main>
<footer>
  ${footerHtml}
</footer>
<br>
<script src="/shared/online/scripts/js/animate-scroll.js"></script>
<script src="/shared/online/scripts/js/${themePrefix}.js"></script>
<script src="https://player.vimeo.com/api/player.js"></script>
</body>
</html>`;
}
